import { useMemo, useState } from "react";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import {
  AlertCircle,
  X,
  Bot,
  MessageCircle,
  Sparkles,
  UserPlus,
  Mail,
  Users,
  LifeBuoy,
  Tag,
  Check,
  Clock,
  Copy,
  Flame,
  ShieldCheck,
} from "lucide-react";
import { cn, getOfferEndDate } from "@/lib/utils";
import { UnderlinedWord } from "@/components/ui/gradient-line";
import { useCountdown } from "@/hooks/useCountdown";
import { useAnalytics } from "@/lib/analytics";
import { useAuth } from "@/components/auth/AuthProvider";
import {
  getCurrencySymbol,
  CURRENCY_PRICING,
  type SupportedCurrency,
} from "@/lib/currency-utils";
import { useTranslation } from "@/lib/i18n";

function FeatureRow({
  label,
  value,
  icon,
  reverseBoolean = false,
  variant = "default",
}: {
  label: string;
  value: string | boolean;
  icon: React.ReactNode;
  reverseBoolean?: boolean;
  variant?: "default" | "popular";
}) {
  let content;
  let isPositive = true;

  if (typeof value === "boolean") {
    if (value) {
      content = <Check className="w-3 h-3" />;
      isPositive = !reverseBoolean;
    } else {
      content = <X className="w-3 h-3" />;
      isPositive = reverseBoolean;
    }
  } else {
    content = <span className="text-xs font-bold">{value}</span>;
    isPositive = value !== "0" && value !== "Limited";
  }

  return (
    <div className="flex items-center justify-between group">
      <div
        className={cn(
          "flex items-center gap-3",
          variant === "popular"
            ? "text-neutral-900 dark:text-neutral-100"
            : "text-neutral-700 dark:text-neutral-300"
        )}
      >
        <div
          className={cn(
            variant === "popular"
              ? "text-neutral-500 dark:text-neutral-400"
              : "text-neutral-400 dark:text-neutral-500"
          )}
        >
          {icon}
        </div>
        <span className="text-sm font-medium">{label}</span>
      </div>

      <div className="flex items-center justify-center h-6 min-w-6 px-1.5 rounded-full text-xs transition-colors bg-blue-50 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
        {content}
      </div>
    </div>
  );
}

export const PLAN_FEATURES = {
  free: {
    automations: "3",
    dms: "2,000",
    aiReplies: "1,000",
    askToFollow: true,
    emailCollection: false,
    igProfile: "1",
    teamAccess: "0",
    support: "Limited",
    branding: true,
  },
  starter: {
    automations: "Unlimited",
    dms: "Unlimited",
    aiReplies: "10,000",
    askToFollow: true,
    emailCollection: false,
    igProfile: "3",
    teamAccess: "3",
    support: "Email",
    branding: false,
  },
  professional: {
    automations: "Unlimited",
    dms: "Unlimited",
    aiReplies: "Unlimited",
    askToFollow: true,
    emailCollection: true,
    igProfile: "6",
    teamAccess: "Unlimited",
    support: "Priority",
    branding: false,
  },
};

const plans = [
  {
    name: "Free Trial",
    icon: "https://cms.zapify.pro/uploads/free_81643bc9c6.png",
    iconColor: "text-green-500",
    iconBg: "bg-green-50 dark:bg-green-900/20",
    price: 0,
    description: "Perfect to get started with Zapify.",
    featureData: PLAN_FEATURES.free,
    highlight: false,
    cta: "Start 30 Days Free Trial",
    metadata: {
      id: "free_trial",
      type: "free",
      monthlyPrice: 0,
      yearlyPrice: 0,
    },
  },
  {
    name: "Starter",
    icon: "https://cms.zapify.pro/uploads/starter_3598d5d533.png",
    iconColor: "text-purple-500",
    iconBg: "bg-purple-50 dark:bg-purple-900/20",
    price: 9.99,
    description: "Best for individual content creators.",
    featureData: PLAN_FEATURES.starter,
    highlight: false,
    cta: "Subscribe",
    metadata: {
      id: "prod_SX7C6fQpqDPi0S",
      type: "subscription",
      monthlyPrice: 9.99,
      yearlyPrice: 99.0,
      monthlyPriceId: "price_1RnnMgQuf5DD5NVM0tK8fnBS",
      yearlyPriceId: "price_1Ro76YQuf5DD5NVMoz5hjXjB",
    },
  },
  {
    name: "Professional",
    icon: "https://cms.zapify.pro/uploads/professional_7172f97c18.png",
    iconColor: "text-blue-500",
    iconBg: "bg-blue-50 dark:bg-blue-900/20",
    price: 14.99,
    description: "Best for startups and small brands.",
    featureData: PLAN_FEATURES.professional,
    highlight: true,
    cta: "Subscribe",
    metadata: {
      id: "prod_SX7KKxNttt5L97",
      type: "subscription",
      monthlyPrice: 19.99,
      yearlyPrice: 199.0,
      monthlyPriceId: "price_1RnnSNQuf5DD5NVMx6NnNRxf",
      yearlyPriceId: "price_1Ro8sBQuf5DD5NVMmDVEE9Eu",
    },
  },
] as const;

type Plan = (typeof plans)[number];

function CouponBadge({ code = "COUPON_CODE" }: { code?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="inline-flex items-center gap-2 border border-dashed border-amber-400/60 rounded-lg px-3 py-1.5 font-mono text-sm text-amber-300 hover:bg-amber-400/10 hover:border-amber-400 transition-all duration-200 backdrop-blur-sm cursor-pointer"
      aria-label={`Copy coupon ${code}`}
      title="Copy coupon"
    >
      <Copy className="h-4 w-4 text-amber-400" />
      <span className="tracking-wider font-bold">
        {copied ? "COPIED!" : code}
      </span>
    </button>
  );
}

function OfferStrip() {
  const target = useMemo(() => getOfferEndDate(), []);
  const { days, hours, minutes, seconds, expired } = useCountdown(target);

  return (
    <div className="relative rounded-2xl overflow-hidden transition-all duration-500 ease-in-out hover:scale-[1.01]">
      <div aria-hidden className="absolute inset-0 rounded-2xl overflow-hidden">
        <div
          className="absolute inset-0 rounded-2xl"
          style={{
            background: `linear-gradient(90deg, 
              #F58529 0%, 
              #DD2A7B 25%, 
              #8134AF 50%, 
              #515BD4 75%, 
              #F58529 100%
            )`,
            backgroundSize: "300% 100%",
            animation: "flow-left-right 4s linear infinite",
          }}
        />
        <div className="absolute inset-[3px] rounded-2xl bg-black" />
      </div>

      <div className="relative z-10 w-full rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="shrink-0 p-3 rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 shadow-lg animate-pulse">
            <Flame className="h-6 w-6 text-black" />
          </div>
          <div>
            <div className="inline-flex items-center gap-3 mb-2 flex-wrap">
              <span className="px-3 py-1 text-xs font-black uppercase tracking-wider rounded-full bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg animate-[pulse_3s_ease-in-out_infinite]">
                🔥 10% OFF TODAY
              </span>
              <span className="px-3 py-1 text-xs font-bold rounded-full bg-amber-400 text-black shadow-md">
                ⚡ Limited Spots
              </span>
            </div>
            <div className="text-xl font-black text-white mb-1">
              Limited Time Offer
            </div>
            <div className="text-white/70 font-semibold mb-4 text-lg">
              Register today and get{" "}
              <span className="text-amber-400 font-black">10% off</span> on all
              plans
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <CouponBadge code="ZAP-IND-78" />
              <div className="flex items-center gap-2 text-sm text-emerald-400">
                <ShieldCheck className="h-4 w-4 text-emerald-400" />
                <span className="font-semibold">Secure checkout</span>
              </div>
              <div className="text-sm text-neutral-300 font-medium">
                No credit card required
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-4 w-full md:w-auto">
          <div className="flex items-center gap-4 justify-end">
            <div className="flex items-center gap-3 text-white">
              <Clock className="h-5 w-5 text-amber-400" />
              <span className="text-lg font-black">Offer ends in</span>
            </div>
            <div className="flex items-center gap-2">
              {[
                `${days}`.padStart(2, "0"),
                `${hours}`.padStart(2, "0"),
                `${minutes}`.padStart(2, "0"),
                `${seconds}`.padStart(2, "0"),
              ].map((val, idx) => (
                <div key={idx} className="text-center">
                  <div className="px-3 py-2 rounded-sm bg-gradient-to-b from-neutral-800 to-neutral-900 border border-neutral-700 font-black min-w-[50px] text-xl shadow-lg">
                    <span className="bg-gradient-to-r from-brand-accent/20 via-brand-accent to-brand-accent/20 bg-clip-text text-transparent">
                      {expired ? "00" : val}
                    </span>
                  </div>
                  <div className="text-xs text-neutral-400 mt-1 text-center font-medium">
                    {idx === 0
                      ? "Days"
                      : idx === 1
                        ? "Hours"
                        : idx === 2
                          ? "Minutes"
                          : "Seconds"}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4 self-end">
            <div className="w-48 h-3 bg-neutral-800 rounded-full overflow-hidden border border-neutral-700 shadow-inner">
              <div
                className="h-full bg-[#34ABF8] rounded-full shadow-lg"
                style={{
                  width: "82%",
                  animation: "flow-left-right 4s linear infinite",
                }}
              />
            </div>
            <div className="text-sm font-black text-amber-400">82% claimed</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ plan, isYearly, currency = 'usd' }: { plan: Plan; isYearly: boolean; currency?: SupportedCurrency }) {
  const { track } = useAnalytics();
  const { openAuth } = useAuth();
  const { translations: t } = useTranslation();
  const metadata = plan.metadata as any;

  // Translate plan name, description, and CTA
  const planNameMap: Record<string, string> = {
    "Free Trial": t.pricing.freeTrial,
    "Starter": t.pricing.starter,
    "Professional": t.pricing.professional,
  };
  const planDescMap: Record<string, string> = {
    "Free Trial": t.pricing.freeTrialDesc,
    "Starter": t.pricing.starterDesc,
    "Professional": t.pricing.professionalDesc,
  };
  const translatedName = planNameMap[plan.name] || plan.name;
  const translatedDesc = planDescMap[plan.name] || plan.description;
  const translatedCta = plan.cta === "Subscribe" ? t.pricing.subscribe : t.pricing.startFreeTrial;
  const currencySymbol = getCurrencySymbol(currency);

  // Get pricing based on plan type and currency
  const getPlanPricing = () => {
    if (metadata?.type === 'free') {
      return { monthly: 0, yearly: 0 };
    }

    const planKey = plan.name.toLowerCase() as 'starter' | 'professional';
    if (planKey === 'starter' || planKey === 'professional') {
      return CURRENCY_PRICING[currency][planKey];
    }

    // Fallback to USD pricing from metadata
    return {
      monthly: metadata?.monthlyPrice ?? plan.price,
      yearly: metadata?.yearlyPrice ?? 0,
    };
  };

  const pricing = getPlanPricing();
  const monthlyPrice = pricing.monthly;
  const yearlyPrice = pricing.yearly;
  const displayPrice = isYearly ? yearlyPrice / 12 : monthlyPrice;
  const unit = t.pricing.perMonth;

  const isBestSeller = plan.highlight;

  const cardInner = (
    <div
      className={cn(
        "relative rounded-xl p-5 border shadow-sm transition h-full flex flex-col hover:shadow-lg hover:-translate-y-1 duration-300",
        isBestSeller
          ? "border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900"
          : "border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900"
      )}
      data-product-id={metadata?.id ?? undefined}
      data-price-id={
        isYearly ? metadata?.yearlyPriceId : metadata?.monthlyPriceId
      }
    >
      {isBestSeller && (
        <div className="absolute -top-3 left-4">
          <div className="px-2 py-1 text-[10px] font-extrabold uppercase tracking-wider rounded-md text-white bg-gradient-to-r from-[#F58529] via-[#DD2A7B] via-[#8134AF] to-[#515BD4] shadow-sm transform -rotate-2">
            {t.pricing.bestSeller}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mb-3">
        <div
          className={cn(
            "w-10 h-10 rounded-md flex items-center justify-center transition-transform hover:scale-110 duration-300",
            plan.iconBg
          )}
        >
          {/* Using a placeholder if image fails, but keeping Image component as requested */}
          <div className="relative w-6 h-6">
            <img
              src={plan.icon}
              alt={`${plan.name || "Plan"} icon`}
              aria-hidden="true"
              className="object-contain"
            />
          </div>
        </div>
        <div>
          <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
            {translatedName}
          </h3>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            {translatedDesc}
          </p>
        </div>
      </div>

      <div className="mb-4 relative overflow-hidden h-[42px] flex items-end">
        {metadata?.type === "free" ? (
          <div className="flex items-center gap-2">
            <span className="text-3xl font-black text-green-600 dark:text-green-500">
              {t.pricing.free}
            </span>
            <div className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-bold rounded-full">
              {t.pricing.noCreditCard}
            </div>
          </div>
        ) : (
          <div className="flex items-end gap-1 relative w-full">
            <span
              className={cn(
                "text-3xl font-black text-neutral-900 dark:text-neutral-100 transition-all duration-300 transform"
                // Simple animation simulation via CSS classes
              )}
            >
                {currencySymbol}{Math.floor(displayPrice ?? 0)}
              <span className="text-lg align-bottom">
                .{(displayPrice ?? 0).toFixed(2).split(".")[1]}
              </span>
            </span>
            <span className="text-sm text-neutral-600 dark:text-neutral-400 pb-1">
              {unit}
            </span>
          </div>
        )}

        {isYearly &&
          monthlyPrice > 0 &&
          yearlyPrice > 0 &&
          metadata?.type !== "free" && (
            <div className="ml-auto mb-1 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="text-xs text-neutral-500 dark:text-neutral-400 text-right">
              ({currencySymbol}{yearlyPrice}/year)
              </div>
              <div className="text-xs font-semibold text-green-600 dark:text-green-500 text-right">
                {t.pricing.save}{" "}
                {Math.round(
                  ((monthlyPrice * 12 - yearlyPrice) / (monthlyPrice * 12)) *
                    100
                )}
                %
              </div>
            </div>
          )}
      </div>

      <div className="mb-4">
        <Button
          variant={isBestSeller && metadata?.type !== "free" ? "gradient-brand" : undefined}
          className={cn(
            "w-full text-white transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]",
            metadata?.type === "free"
              ? "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
              : !isBestSeller && "bg-black hover:bg-neutral-800 dark:bg-neutral-100 dark:text-black dark:hover:bg-neutral-200"
          )}
          aria-label={
            metadata?.type === "free"
              ? `Start ${plan.name}`
              : `Subscribe to ${plan.name}`
          }
          onClick={() => {
            track("pricing_plan_selected", {
              plan_name: plan.name,
              plan_id: metadata?.id,
              plan_type: metadata?.type,
              billing_period: isYearly ? "yearly" : "monthly",
              price: isYearly ? metadata?.yearlyPrice : metadata?.monthlyPrice,
              section: "pricing_page",
            });

            openAuth();
          }}
        >
          {translatedCta}
        </Button>
      </div>

      <div className="space-y-3 mb-2 animate-in fade-in zoom-in-95 duration-500 delay-100">
        <div className="space-y-3">
          <FeatureRow
            label={t.pricing.automations}
            value={plan.featureData.automations}
            icon={<Bot className="w-4 h-4" />}
          />
          <FeatureRow
            label={t.pricing.dms}
            value={plan.featureData.dms}
            icon={<MessageCircle className="w-4 h-4" />}
          />
          <FeatureRow
            label={t.pricing.aiReplies}
            value={plan.featureData.aiReplies}
            icon={<Sparkles className="w-4 h-4" />}
          />
          <FeatureRow
            label={t.pricing.askToFollow}
            value={plan.featureData.askToFollow}
            icon={<UserPlus className="w-4 h-4" />}
          />
          <FeatureRow
            label={t.pricing.emailCollection}
            value={plan.featureData.emailCollection}
            icon={<Mail className="w-4 h-4" />}
          />
          <FeatureRow
            label={t.pricing.igProfile}
            value={plan.featureData.igProfile}
            icon={
              <img
                src="https://cdn.simpleicons.org/instagram/E4405F"
                alt=""
                width={16}
                height={16}
                className="size-4 object-contain"
                loading="lazy"
              />
            }
          />
          <FeatureRow
            label={t.pricing.teamAccess}
            value={plan.featureData.teamAccess}
            icon={<Users className="w-4 h-4" />}
          />
          <FeatureRow
            label={t.pricing.support}
            value={plan.featureData.support}
            icon={<LifeBuoy className="w-4 h-4" />}
          />
          <FeatureRow
            label={t.pricing.platformBranding}
            value={plan.featureData.branding}
            icon={<Tag className="w-4 h-4" />}
            reverseBoolean
          />
        </div>
      </div>

      <div className="mt-auto" />
    </div>
  );

  if (isBestSeller) {
    return (
      <div className="rounded-xl p-[2px] bg-gradient-to-r from-[#F58529] via-[#DD2A7B] via-[#8134AF] to-[#515BD4] shadow-sm transform hover:scale-[1.01] transition-transform duration-300">
        {cardInner}
      </div>
    );
  }

  return (
    <div className="transform hover:scale-[1.01] transition-transform duration-300">
      {cardInner}
    </div>
  );
}

export interface PricingProps {
  countryCode?: string;
  currency?: SupportedCurrency;
}

const Pricing = ({ countryCode = 'US', currency = 'usd' }: PricingProps) => {
  const { track } = useAnalytics();
  const [isYearly, setIsYearly] = useState(false);
  const { translations: t } = useTranslation();

  // Get currency symbol based on detected currency
  const currencySymbol = getCurrencySymbol(currency);

  return (
    <section id="pricing" className="py-16 relative bg-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* SEO-optimized hidden heading */}
        <h1 className="sr-only">
          Zapify Pricing Plans - AI-Powered Instagram Automation
        </h1>

        <div className="text-center mb-16 space-y-4">
          <h2 className="text-3xl md:text-5xl font-bold text-neutral-900 dark:text-white tracking-tight">
            {t.pricing.headingTitle} <UnderlinedWord>{t.pricing.headingHighlight}</UnderlinedWord>
          </h2>
          <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
            {t.pricing.headingDescription}
          </p>
        </div>

        <div className="mb-6">{/* <OfferStrip /> */}</div>

        <div className="flex justify-center items-center gap-4 my-6 mx-auto w-fit">
          <div className="flex justify-center items-center gap-4 bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm rounded-full px-6 py-3 border border-neutral-200 dark:border-neutral-700 shadow-sm animate-in zoom-in-95 duration-500 delay-200">
            <span
              className={cn(
                "text-base font-semibold transition-colors duration-200 cursor-pointer",
                isYearly
                  ? "text-neutral-500 dark:text-neutral-400"
                  : "text-neutral-900 dark:text-neutral-100"
              )}
              onClick={() => setIsYearly(false)}
            >
              {t.pricing.monthly}
            </span>
            <div className="scale-105 active:scale-95 transition-transform">
              <Switch
                checked={isYearly}
                onCheckedChange={(checked) => {
                  setIsYearly(checked);
                  track("pricing_toggle_changed", {
                    billing_period: checked ? "yearly" : "monthly",
                    section: "pricing_page",
                  });
                }}
                id="pricing-toggle"
              />
            </div>
            <span
              className={cn(
                "text-base font-semibold transition-colors duration-200 cursor-pointer",
                !isYearly
                  ? "text-neutral-500 dark:text-neutral-400"
                  : "text-neutral-900 dark:text-neutral-100"
              )}
              onClick={() => setIsYearly(true)}
            >
              {t.pricing.yearly}
            </span>
          </div>

          {/* "Get 2 Months Free" Badge with Arrow */}
          <div className="relative overflow-visible">
            {/* Arrow pointing to the badge - Always visible */}
            <img
              src="/arrowtoleft.png"
              alt="Arrow"
              className="absolute -top-5 -left-5 w-10 h-10 z-10 -rotate-5"
            />
            <div
              className={cn(
                "relative rounded-full transition-all duration-500 rotate-5",
                "p-[2px]", // Padding for gradient border
                "bg-gradient-to-r from-[#F58529] via-[#DD2A7B] via-[#8134AF] to-[#515BD4]",
                isYearly ? "scale-100" : "scale-95"
              )}
            >
              <div className="px-4 py-1.5 rounded-full font-semibold text-sm bg-white dark:bg-neutral-900 flex items-center gap-2">
                {isYearly && (
                  <img
                    src="https://fonts.gstatic.com/s/e/notoemoji/latest/1f389/512.webp"
                    alt="🎉"
                    className="w-4 h-4 animate-in zoom-in duration-300"
                  />
                )}
                <span className="bg-gradient-to-r from-[#F58529] via-[#DD2A7B] via-[#8134AF] to-[#515BD4] bg-clip-text text-transparent">
                  {t.pricing.get2MonthsFree}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 items-stretch">
          {plans.map((plan, index) => {
            const key = plan.name;
            return (
              <div
                key={key}
                className={`animate-in slide-in-from-bottom-8 fade-in duration-700 fill-mode-backwards`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <PlanCard plan={plan} isYearly={isYearly} currency={currency} />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
